import React from "react";
import moment from "moment";
import { withTranslation } from "react-i18next";
import _ from "lodash";
import NavigationComponent from "../components/NavigationComponent";
import RangeContainerComponent from "../body-measurements/RangeContainerComponent";
import GraphComponent from "../readiness/GraphComponent";
import { RPEList } from "../session-rpe/RPEList";
import { DATE_FORMATS } from "../../../../constants";
import {
  getLabels,
  readyChartData,
  mapUtcToDatePicker,
  filterUnsetValues,
  sortStatsByDate,
  filterStatsbyDateRange,
} from "../../../../utils/daily-stats";
import { AthleteLinks } from "../../../../parts/athlete-links";
import { AthleteSelect } from "../../../../parts/inputs/athlete-select-new";
import getAvatarUrl from "../../../../utils/get-avatar-url";
import { RillContext } from "../../../../rill/RillReact/share/base";

const UNIT = "BPM";

class RestingHeartRate extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.field = "restingHeartRate";
    this.precision = 0;
    this.state = {
      currentAthlete: null,
      chartData: [],
      dateFrom: null,
      dateTo: null,
      quickDisabled: false,
      rangeSelector: {
        type: "months",
        value: 6,
      },
      statsData: [],
      hoveredDate: "",
      sortedRecent: true,
      sortedTableData: null,
    };

    this.updateState = this.updateState.bind(this);
    this.updateHoveredStat = this.updateHoveredStat.bind(this);
    this.getDateFirstEntry = this.getDateFirstEntry.bind(this);
    this.sortList = this.sortList.bind(this);
  }

  componentDidMount() {
    const { value, type } = this.state.rangeSelector;
    const athleteId = this.context.locals.athlete
      ? this.context.locals.athlete._id
      : this.context.locals.activeUser._id;

    const stats = sortStatsByDate(
      filterUnsetValues(this.context.locals.dailyStats, this.field)
    );

    this.setState({
      currentAthlete: athleteId,
      dateFrom: moment(new Date(), DATE_FORMATS.DATE_DAY_FORMAT)
        .subtract(value, type)
        .format(DATE_FORMATS.DATE_PICKER_FORMAT),
      statsData: stats,
    });
  }

  componentDidUpdate(prevState) {
    const { state } = this;
    const athleteId = this.context.locals.athlete
      ? this.context.locals.athlete._id
      : this.context.locals.activeUser._id;

    if (athleteId !== state.currentAthlete) {
      if (state.rangeSelector.type !== "lifetime") {
        this.setState({ currentAthlete: athleteId });
      } else {
        this.setState({
          currentAthlete: athleteId,
          dateFrom: moment("2015-12-31T07:57:45.121Z").format(
            DATE_FORMATS.DATE_PICKER_FORMAT
          ),
        });
      }
    }

    if (
      prevState.currentAthlete !== null &&
      state.currentAthlete !== athleteId
    ) {
      const stats = sortStatsByDate(
        filterUnsetValues(this.context.locals.dailyStats, this.field)
      );
      this.setState({ statsData: stats });
    }
  }

  updateHoveredStat(date = "") {
    this.setState({ hoveredDate: date });
  }

  calculateAvg(stats, field, precision) {
    if (!stats.length) return 0;
    const total = stats
      .map((stat) => stat[field])
      .reduce((prev, current) => prev + current, 0);
    return _.round(total / stats.length, precision);
  }

  addChangeTolist(stats, field, precision) {
    const statsCopy = _.cloneDeep(stats);
    for (let i = stats.length - 1; i >= 0; i--) {
      if (i === statsCopy.length - 1) {
        statsCopy[i].change = 0;
      } else {
        statsCopy[i].change = _.round(
          statsCopy[i][field] - statsCopy[i + 1][field],
          precision
        );
      }
    }
    return statsCopy;
  }

  getDateFirstEntry() {
    const firstEntry = this.context.locals.dailyStats
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .find((stats) => stats.restingHeartRate != null);
    return firstEntry ? firstEntry.date : null;
  }

  updateState(state) {
    this.setState(state);
  }

  sortList(newState) {
    this.setState({
      ...this.state,
      sortedTableData: newState.dateFormattedData,
      sortedRecent: newState.sortedRecent,
    });
  }

  render() {
    const { t } = this.props;
    const { locals } = this.context;
    const { query, pathname, activeUserToRequest, athlete } = locals;
    const { dateTo, dateFrom, hoveredDate, statsData } = this.state;

    const intensityMeasure =
      _.get(activeUserToRequest, "intensityMeasure", "rpe") === "rpe"
        ? "RPE"
        : "RiR";
    const isRiR = intensityMeasure === "RiR";

    const listData = filterStatsbyDateRange({ stats: statsData, dateTo, dateFrom });
    const avg = this.calculateAvg(listData, this.field, this.precision);
    const roundedList = listData.map((stat) => ({
      ...stat,
      [this.field]: _.round(stat[this.field], this.precision),
    }));

    const headerRight = {
      name: t("restingHeartRate.average"),
      value: avg ? `${_.round(avg, 0)} ${UNIT}` : 0,
    };

    const [chartData, labels] = [
      readyChartData({ stats: roundedList, field: this.field }),
      getLabels(roundedList).reverse(),
    ];

    const colors = ["rgba(91, 197, 0, 1)"];
    const start = roundedList[roundedList.length - 1]
      ? roundedList[roundedList.length - 1][this.field]
      : 0;
    const end = roundedList[0] ? roundedList[0][this.field] : 0;
    const change = _.round(end - start, this.precision);

    const sourceLabels = {
      wearable: t("restingHeartRate.sourceWearable"),
      manual: t("restingHeartRate.sourceManual"),
    };

    const statsList = this.addChangeTolist(
      mapUtcToDatePicker({
        stats: listData,
        fieldToConvert: "utcDate",
        newField: "date",
      }).map((s) => ({
        ...s,
        restingHeartRateSourceLabel: sourceLabels[s.restingHeartRateSource] ?? "—",
      })),
      this.field,
      this.precision
    );

    const columnsData = [
      {
        name: t("reports.date"),
        field: "date",
        columnClass: "date",
        itemClass: "date",
      },
      {
        name: UNIT,
        field: this.field,
        columnClass: "bodyweight",
        itemClass: "bodyweight",
      },
      {
        name: t("restingHeartRate.sourceColumn"),
        field: "restingHeartRateSourceLabel",
        columnClass: "source",
        itemClass: "source",
      },
      {
        name: t("restingHeartRate.change"),
        field: "change",
        columnClass: "change",
        itemClass: "change",
        unit: UNIT,
      },
    ];

    const chartOptions = {
      showLine: true,
      ticks: [],
      axisY: {
        offset: 50,
        labelOffset: { x: 5, y: 5 },
        labelInterpolationFnc: (value) => value.toFixed(0),
      },
    };

    return (
      <main role="reports-wrapper">
        <div className="athlete-section-header only-show-mobile">
          <AthleteLinks
            athlete={athlete}
            pathName={pathname}
            classNames="only-show-mobile"
            query={query}
          />
          <div className="athlete-select dropdown-athlete">
            <div
              className="avatar"
              style={{
                backgroundImage: `url(${getAvatarUrl(locals?.athlete?._id)})`,
              }}
            />
            <AthleteSelect locals={locals} />
          </div>
        </div>
        <NavigationComponent
          query={query}
          pathName={pathname}
          textKey="nav.calendarDayView"
          intensityMeasure={intensityMeasure}
          isRiR={isRiR}
          classNames="mobile-hidden"
        />
        <article id="resting-heart-rate" role="report-content" security="">
          <RangeContainerComponent
            {...this.state}
            key={1}
            updateState={(state) => {
              this.updateState({ ...state, sortedTableData: null });
            }}
            getDateFirstEntry={this.getDateFirstEntry}
            reportSelected={t("metrics.restingHeartRate")}
            athleteId={locals?.athlete?._id}
            locals={locals}
          />

          <GraphComponent
            bodyMeasurement={true}
            dateTo={dateTo}
            dateFrom={dateFrom}
            series={chartData}
            labels={labels}
            chartName={t("restingHeartRate.axisLabel")}
            displayLeyend={false}
            colors={colors}
            areaOpacity={0.3}
            updateHoveredStat={this.updateHoveredStat}
            chartOptions={chartOptions}
            headerRight={headerRight}
            tooltipUnit={UNIT}
          />
          <div className="bar-container">
            <div>
              <span>
                <b>
                  {start} {UNIT}
                </b>
              </span>
              <h4>{t("restingHeartRate.start")}</h4>
            </div>
            <div data-middle="true">
              <span>
                <b>
                  {end} {UNIT}
                </b>
              </span>
              <h4>{t("restingHeartRate.end")}</h4>
            </div>
            <div>
              <span>
                <b>
                  {change} {UNIT}
                </b>
              </span>
              <h4>{t("restingHeartRate.change")}</h4>
            </div>
          </div>

          <RPEList
            dateFrom={this.state.dateFrom}
            dateTo={this.state.dateTo}
            list={this.state.sortedTableData || statsList}
            columnsData={columnsData}
            customClass="resting-heart-rate"
            highlightedElem={hoveredDate}
            sortedRecent={this.state.sortedRecent}
            updateState={this.sortList}
          />
        </article>
      </main>
    );
  }
}

RestingHeartRate.contextType = RillContext;

export default withTranslation("athlete-portal")(RestingHeartRate);
